/**
 * UTILITIES FOR SECURE CATALOGUE PASSWORD MANAGEMENT
 * This file provides functions to encrypt and verify catalogue passwords
 * using the existing AES encryption system in the application
 */

import { encodePassword, decodePassword } from '../../data/utils';

/**
 * Encrypts a catalogue password before saving it to the database
 * @param {string} plainPassword - Plain text password
 * @returns {string} - Encrypted password
 * @throws {Error} - If encryption fails
 */
export function encryptCataloguePassword(plainPassword) {
    if (!plainPassword || typeof plainPassword !== 'string') {
        throw new Error('Password must be a non-empty string');
    }

    try {
        // Add a prefix to identify encrypted passwords
        const encrypted = encodePassword(plainPassword);
        return `CAT_ENCRYPTED:${encrypted}`;
    } catch (error) {
        console.error('Error encrypting catalogue password:', error);
        throw new Error('Failed to encrypt catalogue password');
    }
}

/**
 * Checks if a password is already encrypted
 * @param {string} password - Password to check
 * @returns {boolean} - True if the password is already encrypted
 */
export function isCataloguePasswordEncrypted(password) {
    return typeof password === 'string' && password.startsWith('CAT_ENCRYPTED:');
}

/**
 * Decrypts a catalogue password for verification
 * @param {string} encryptedPassword - Encrypted password
 * @returns {string} - Plain text password
 * @throws {Error} - If decryption fails
 */
export function decryptCataloguePassword(encryptedPassword) {
    if (!encryptedPassword || typeof encryptedPassword !== 'string') {
        throw new Error('Encrypted password must be a non-empty string');
    }

    // If not encrypted, return as-is (backwards compatibility)
    if (!isCataloguePasswordEncrypted(encryptedPassword)) {
        console.warn('Password not encrypted, returning as-is for backwards compatibility');
        return encryptedPassword;
    }

    try {
        // Remove prefix and decrypt
        const encrypted = encryptedPassword.replace('CAT_ENCRYPTED:', '');
        return decodePassword(encrypted);
    } catch (error) {
        console.error('Error decrypting catalogue password:', error);
        throw new Error('Failed to decrypt catalogue password');
    }
}

/**
 * Verifies if a plain text password matches the stored encrypted password
 * @param {string} plainPassword - Plain text password to verify
 * @param {string} storedPassword - Stored password (encrypted or plain text)
 * @returns {boolean} - True if passwords match
 */
export function verifyCataloguePassword(plainPassword, storedPassword) {
    if (!plainPassword || !storedPassword) {
        return false;
    }

    try {
        if (isCataloguePasswordEncrypted(storedPassword)) {
            // Encrypted password - decrypt and compare
            const decryptedPassword = decryptCataloguePassword(storedPassword);
            return plainPassword === decryptedPassword;
        } else {
            // Plain text password (backwards compatibility) - direct comparison
            console.warn('Comparing with unencrypted password (backwards compatibility)');
            return plainPassword === storedPassword;
        }
    } catch (error) {
        console.error('Error verifying catalogue password:', error);
        return false;
    }
}

/**
 * Migrates an existing password from plain text to encrypted
 * @param {string} password - Password to migrate
 * @returns {string} - Encrypted password or original if already encrypted
 */
export function migrateCataloguePasswordToEncrypted(password) {
    if (!password || typeof password !== 'string') {
        return password;
    }

    // If already encrypted, do nothing
    if (isCataloguePasswordEncrypted(password)) {
        return password;
    }

    // Encrypt the plain text password
    try {
        return encryptCataloguePassword(password);
    } catch (error) {
        console.error('Error migrating password to encrypted:', error);
        return password; // Return original on error
    }
} 